import { Button, Container, Paper, Text } from "@mantine/core";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import {
  TextInput,
  Select,
  MultiSelect,
  Textarea,
} from "react-hook-form-mantine";
import { Link, useNavigate } from "react-router-dom";

import { useCreateSponsor, usePatchSponsor } from "../../api/sponsors";
import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import { extractDirtyData } from "../../utils/dirtyData";
import ExternalLinksInput from "../ExternalLinksInput";
import ImageUpload from "../ImageUpload";
import LocalizedInput from "../common/LocalizedInput";
import { useTicketSearch } from "../../hooks/useTicketSearch";
import { web_sponsorFormSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";
import {
  makeI18nDefaultValue,
  mergeI18nWithDefaults,
} from "../../utils/i18nUtils";

function SponsorForm({
  mode = "create",
  initialData = null,
  sponsorLevels = [],
  event,
}) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState(null);

  const currentWorkspace = getCurrentWorkspace(user);
  const isEditMode = mode === "edit";
  const sponsorId = initialData?.id;

  const { options, searchValue, setSearchValue } = useTicketSearch(
    currentWorkspace?.eventId
  );

  const mainLanguage = event?.language || "en";
  const otherLanguages = event?.secondaryLanguages || [];

  // Prepare default values based on mode
  const getDefaultValues = () => {
    if (isEditMode && initialData) {
      return {
        nameI18n: mergeI18nWithDefaults(
          initialData.nameI18n,
          mainLanguage,
          otherLanguages
        ),
        descriptionI18n: mergeI18nWithDefaults(
          initialData.descriptionI18n,
          mainLanguage,
          otherLanguages
        ),
        levelId: initialData.levelId?.toString() || "",
        links: initialData.links || [],
        logo: initialData.logo || "",
        repIds:
          initialData.sponsorReps.map((rep) => rep.repTicketId.toString()) ||
          [],
      };
    }

    // Create mode defaults
    return {
      nameI18n: makeI18nDefaultValue(mainLanguage, otherLanguages),
      levelId: "",
      descriptionI18n: makeI18nDefaultValue(mainLanguage, otherLanguages),
      links: [],
      logo: "",
      eventId: currentWorkspace?.eventId,
      repIds: [],
    };
  };

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isDirty, dirtyFields },
  } = useForm({
    defaultValues: getDefaultValues(),
    resolver: resolver(web_sponsorFormSchema, { allErrors: true }),
  });

  // API mutations
  const createMutation = useCreateSponsor();
  const patchMutation = usePatchSponsor(sponsorId);

  const currentMutation = isEditMode ? patchMutation : createMutation;
  const { mutate, isPending } = currentMutation;

  // Prepare sponsor level options
  const sponsorLevelOptions = sponsorLevels?.length
    ? sponsorLevels
        .sort((a, b) => a.order - b.order)
        .map((level) => ({
          value: level.id.toString(),
          label: level.name,
        }))
    : [];

  const onSubmit = (formData) => {
    if (!formData.levelId) {
      setErrorMessage("Sponsor level is required!");
      return;
    }

    setErrorMessage(null);

    let dataToSubmit;

    if (isEditMode) {
      // For edit mode, only send dirty data
      dataToSubmit = extractDirtyData(formData, dirtyFields);
    } else {
      // For create mode, send all data
      dataToSubmit = { ...formData };
    }

    mutate(dataToSubmit, {
      onSuccess: () => {
        navigate("/app/sponsors");
      },
    });
  };

  const localizedInput = [
    {
      nameBase: "nameI18n",
      label: "Name",
      component: TextInput,
      props: {
        withAsterisk: true,
        placeholder: "Name",
        mt: "sm",
      },
    },
  ];

  const localizedDescription = [
    {
      nameBase: "descriptionI18n",
      label: "Description",
      component: Textarea,
      props: {
        rows: 2,
        placeholder: "Description",
        mt: "sm",
        autosize: true,
      },
    },
  ];

  return (
    <Container size={500}>
      <Paper withBorder shadow="md" p={16} mt={16} radius="md">
        <form onSubmit={handleSubmit(onSubmit)}>
          <LocalizedInput
            control={control}
            fields={localizedInput}
            mainLanguage={mainLanguage}
            otherLanguages={otherLanguages}
          />

          {sponsorLevelOptions.length === 0 ? (
            <>
              <Text size="sm" c="dimmed" mt="sm">
                No sponsor levels available. Please create a sponsor level{" "}
                <Text component={Link} to="/app/sponsors/levels" c="blue">
                  here
                </Text>
                .
              </Text>
            </>
          ) : (
            <Select
              label="Sponsor level"
              placeholder="Select sponsor level"
              name="levelId"
              data={sponsorLevelOptions}
              control={control}
              mt="md"
              withAsterisk
            />
          )}

          <LocalizedInput
            control={control}
            fields={localizedDescription}
            mainLanguage={mainLanguage}
            otherLanguages={otherLanguages}
          />

          <ImageUpload
            control={control}
            name="logo"
            label="Logo"
            description="Upload a logo for the sponsor"
            defaultImage={isEditMode ? initialData?.logo : undefined}
            mt="md"
            resize
          />

          <ExternalLinksInput
            control={control}
            setValue={setValue}
            name="links"
            mt="md"
          />

          <MultiSelect
            label="Representative"
            placeholder="Select sponsor representative"
            name="repIds"
            data={options}
            control={control}
            mt="md"
            searchable
            nothingFoundMessage="No representatives found"
            searchValue={searchValue}
            onSearchChange={setSearchValue}
          />

          <Text c="red" mt="md">
            {errorMessage}
          </Text>

          <Button type="submit" mt="md" loading={isPending} disabled={!isDirty}>
            {isEditMode ? "Update sponsor" : "Create sponsor"}
          </Button>
        </form>
      </Paper>
    </Container>
  );
}

export default SponsorForm;
